import { defineStore } from 'pinia';
import { shallowRef, computed, triggerRef } from 'vue';
import { ApiDriverUser, CurrentDayDriver, UserAvailabilityResponse } from '@/rennovation/planning-view/types/api/users-response';
import { useUserApi } from '@/rennovation/common/services/api/useUserApi';
import { useAlertStore } from './useAlertStore';
import i18n from '@/plugins/i18n';

export const useUserStore = defineStore('user', () => {
	// ============================================
	// STATE
	// ============================================
	const usersById = shallowRef<Record<string, ApiDriverUser>>({});
	const unallocatedUsers = shallowRef<string[]>([]);
	const currentDayDrivers = shallowRef<UserAvailabilityResponse>(null);
	const currentDayDriversById = shallowRef<Record<string, CurrentDayDriver>>({});
	const alertStore = useAlertStore();
	const t = (key: string) => i18n.global.t(key);

	// ============================================
	// GETTERS
	// ============================================
	const allUsers = computed(() => Object.values(usersById.value));
	const getUserById = (userId: string): ApiDriverUser => usersById.value[userId];

	// ============================================
	// ACTIONS
	// ============================================
	const userApi = useUserApi();

	async function fetchUsers(): Promise<void> {
		try {
			const users = await userApi.getUsers();

			usersById.value = Object.fromEntries(users.map((user) => [user.id, user]));
		} catch (error) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}

	const isDriverActiveOnDate = (driver: ApiDriverUser | null, dateStr: string | null): boolean => {
		if (!dateStr || !driver) return false;

		const targetDate = dateStr.slice(0, 10);
		const joiningDate = driver.dateOfEntry ? driver.dateOfEntry.slice(0, 10) : '0000-00-00';
		const leavingDate = driver.dateOfLeaving ? driver.dateOfLeaving.slice(0, 10) : '9999-12-31';

		return targetDate >= joiningDate && targetDate <= leavingDate;
	};

	function addUser(user: ApiDriverUser): void {
		usersById.value[user.id] = user;
		triggerRef(usersById);
	}

	function updateUser(user: ApiDriverUser): void {
		if (usersById.value[user.id]) {
			usersById.value[user.id] = user;
			triggerRef(usersById);
		}
	}

	function deleteUser(userId: ApiDriverUser['id']): void {
		if (usersById.value[userId]) {
			delete usersById.value[userId];
			triggerRef(usersById);
		}
	}

	async function loadDrivers(): Promise<ApiDriverUser[]> {
		try {
			const drivers = await userApi.listDrivers();
			return drivers.data;
		} catch (error) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}

	async function loadUnallocatedUsers(days: string[]): Promise<string[]> {
		try {
			const result = await userApi.loadUnallocatedUsers(days);
			unallocatedUsers.value = result;
			return result;
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}

	async function loadAvailableDriversByDay(day: string): Promise<UserAvailabilityResponse> {
		try {
			const res = await userApi.loadAvailableDriversByDay(day);
			currentDayDrivers.value = res;

			const data = res?.data?.all || [];

			const updatedIdMap = data.reduce((map: Record<string, any>, driver: any) => {
				if (driver?._id) {
					map[driver._id] = driver;
				}
				return map;
			}, {});

			currentDayDriversById.value = updatedIdMap;
			triggerRef(currentDayDrivers);
			triggerRef(currentDayDriversById);
			return res;
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	}

	return {
		usersById,
		allUsers,
		unallocatedUsers,
		currentDayDrivers,
		currentDayDriversById,
		deleteUser,
		getUserById,
		isDriverActiveOnDate,
		loadDrivers,
		fetchUsers,
		addUser,
		updateUser,
		loadUnallocatedUsers,
		loadAvailableDriversByDay,
	};
});
